/**
 * AddressForm - Reusable address entry component
 *
 * Used across parties, settlement agent, and property address entry.
 */

import type * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** US States for dropdown */
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface AddressFormProps {
  /** Current address value */
  value: Address;
  /** Change handler */
  onChange: (address: Address) => void;
  /** Field-level errors */
  errors?: Partial<Record<keyof Address, string>>;
  /** Whether all fields are required */
  required?: boolean;
  /** ID prefix for form fields */
  idPrefix?: string;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Compact mode (single row) */
  compact?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * AddressForm - Reusable address entry with validation
 */
export const AddressForm: React.FC<AddressFormProps> = ({
  value,
  onChange,
  errors = {},
  required = false,
  idPrefix = 'address',
  disabled = false,
  compact = false,
  className,
}) => {
  const handleChange = (field: keyof Address, fieldValue: string) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const getInputClassName = (field: keyof Address) =>
    cn(errors[field] && 'border-red-500 focus:border-red-500 focus:ring-red-500');

  if (compact) {
    return (
      <div className={cn('space-y-3', className)}>
        {/* Street */}
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-street`}>
            Street Address {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={`${idPrefix}-street`}
            type="text"
            value={value.street}
            onChange={(e) => handleChange('street', e.target.value)}
            placeholder="123 Main Street"
            disabled={disabled}
            className={getInputClassName('street')}
          />
          {errors.street && <p className="text-xs text-red-600">{errors.street}</p>}
        </div>

        {/* City, State, Zip in one row */}
        <div className="grid grid-cols-6 gap-2">
          <div className="col-span-3 space-y-1.5">
            <Label htmlFor={`${idPrefix}-city`}>
              City {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={`${idPrefix}-city`}
              type="text"
              value={value.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Denver"
              disabled={disabled}
              className={getInputClassName('city')}
            />
            {errors.city && <p className="text-xs text-red-600">{errors.city}</p>}
          </div>

          <div className="col-span-1 space-y-1.5">
            <Label htmlFor={`${idPrefix}-state`}>
              State {required && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={value.state}
              onValueChange={(v) => handleChange('state', v)}
              disabled={disabled}
            >
              <SelectTrigger id={`${idPrefix}-state`} className={getInputClassName('state')}>
                <SelectValue placeholder="--" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && <p className="text-xs text-red-600">{errors.state}</p>}
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label htmlFor={`${idPrefix}-zipCode`}>
              ZIP {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id={`${idPrefix}-zipCode`}
              type="text"
              value={value.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              placeholder="80202"
              maxLength={10}
              disabled={disabled}
              className={getInputClassName('zipCode')}
            />
            {errors.zipCode && <p className="text-xs text-red-600">{errors.zipCode}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Full layout (2 columns)
  return (
    <div className={cn('space-y-4', className)}>
      {/* Street Address */}
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-street`}>
          Street Address {required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id={`${idPrefix}-street`}
          type="text"
          value={value.street}
          onChange={(e) => handleChange('street', e.target.value)}
          placeholder="123 Main Street"
          disabled={disabled}
          className={getInputClassName('street')}
        />
        {errors.street && <p className="text-sm text-red-600">{errors.street}</p>}
      </div>

      {/* City and State */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-city`}>
            City {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={`${idPrefix}-city`}
            type="text"
            value={value.city}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="Denver"
            disabled={disabled}
            className={getInputClassName('city')}
          />
          {errors.city && <p className="text-sm text-red-600">{errors.city}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-state`}>
            State {required && <span className="text-red-500">*</span>}
          </Label>
          <Select
            value={value.state}
            onValueChange={(v) => handleChange('state', v)}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-state`} className={getInputClassName('state')}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && <p className="text-sm text-red-600">{errors.state}</p>}
        </div>
      </div>

      {/* ZIP Code */}
      <div className="w-1/2 space-y-1.5">
        <Label htmlFor={`${idPrefix}-zipCode`}>
          ZIP Code {required && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id={`${idPrefix}-zipCode`}
          type="text"
          value={value.zipCode}
          onChange={(e) => handleChange('zipCode', e.target.value)}
          placeholder="80202"
          maxLength={10}
          disabled={disabled}
          className={getInputClassName('zipCode')}
        />
        {errors.zipCode && <p className="text-sm text-red-600">{errors.zipCode}</p>}
      </div>
    </div>
  );
};

AddressForm.displayName = 'AddressForm';

/** Default empty address */
export const DEFAULT_ADDRESS: Address = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
};

/** Validate an address */
export function validateAddress(
  address: Address,
  required = true,
): Partial<Record<keyof Address, string>> {
  const errors: Partial<Record<keyof Address, string>> = {};

  if (required || address.street || address.city || address.state || address.zipCode) {
    if (!address.street?.trim()) {
      errors.street = 'Street address is required';
    }
    if (!address.city?.trim()) {
      errors.city = 'City is required';
    }
    if (!address.state) {
      errors.state = 'State is required';
    }
    if (!address.zipCode?.trim()) {
      errors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(address.zipCode.trim())) {
      errors.zipCode = 'Invalid ZIP code format';
    }
  }

  return errors;
}

export { US_STATES };
